import {
  PlayerIndexMap,
  RenderPlayerIdentity,
  Team01,
} from "./GameStoryDialog_UPDATED";
import { KitchenArrivalRow, Vec2 } from "./RallyBoardPopup";
import * as React from "react";
import {
  Box,
  Typography,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Customized,
  ReferenceArea,
  ReferenceLine,
} from "recharts";
import { ActiveRallyVideo } from "./ActiveRallyVideo";
import { FirefightClip } from "./GameStory_firefights";
import { getOverlayShotQualityParts } from "./GameStory_shotQuality";

type FloatingRect = { x: number; y: number; w: number; h: number };
type ResizeDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export function FloatingPanel(props: {
  open: boolean;
  rect: FloatingRect;
  title: string;
  onChangeRect: (next: FloatingRect) => void;
  onClose: () => void;
  children: React.ReactNode;
  color: string;
  minW?: number;
  minH?: number;
  dataDemo?: string;
}) {
  const { open, rect, onChangeRect, onClose, title, children, color } = props;
  const minW = props.minW ?? 260;
  const minH = props.minH ?? 180;

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startRect: FloatingRect;
    mode: "drag" | "resize";
    dir?: ResizeDir;
  } | null>(null);

  const clampRect = React.useCallback(
    (r: FloatingRect): FloatingRect => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const w = Math.max(minW, Math.min(r.w, vw - 8));
      const h = Math.max(minH, Math.min(r.h, vh - 8));

      const x = Math.max(4, Math.min(r.x, vw - w - 4));
      const y = Math.max(4, Math.min(r.y, vh - h - 4));

      return { x, y, w, h };
    },
    [minW, minH],
  );

  const applyResize = React.useCallback(
    (start: FloatingRect, dir: ResizeDir, dx: number, dy: number) => {
      let { x, y, w, h } = start;

      // horizontal
      if (dir.includes("e")) w = start.w + dx;
      if (dir.includes("w")) {
        w = start.w - dx;
        x = start.x + dx;
      }

      // vertical
      if (dir.includes("s")) h = start.h + dy;
      if (dir.includes("n")) {
        h = start.h - dy;
        y = start.y + dy;
      }

      // enforce mins while preserving the “anchored” side
      if (w < minW) {
        const deficit = minW - w;
        w = minW;
        if (dir.includes("w")) x -= deficit; // keep right edge fixed
      }
      if (h < minH) {
        const deficit = minH - h;
        h = minH;
        if (dir.includes("n")) y -= deficit; // keep bottom edge fixed
      }

      return clampRect({ x, y, w, h });
    },
    [clampRect, minW, minH],
  );

  React.useEffect(() => {
    if (!open) return;

    const onMove = (e: PointerEvent) => {
      const st = dragRef.current;
      if (!st) return;

      const dx = e.clientX - st.startX;
      const dy = e.clientY - st.startY;

      if (st.mode === "drag") {
        onChangeRect(
          clampRect({
            ...st.startRect,
            x: st.startRect.x + dx,
            y: st.startRect.y + dy,
          }),
        );
      } else {
        const dir = st.dir ?? "se";
        onChangeRect(applyResize(st.startRect, dir, dx, dy));
      }
    };

    const onUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [open, onChangeRect, clampRect, applyResize]);

  React.useEffect(() => {
    if (!open) return;

    // ✅ enforce minW/minH + keep on-screen on initial open too
    const next = clampRect(rect);
    if (
      next.x !== rect.x ||
      next.y !== rect.y ||
      next.w !== rect.w ||
      next.h !== rect.h
    ) {
      onChangeRect(next);
    }
  }, [open, rect, clampRect, onChangeRect]);

  React.useEffect(() => {
    if (!open) return;
    const onResize = () => onChangeRect(clampRect(rect));
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [open, rect, onChangeRect, clampRect]);

  const startDrag = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRect: rect,
      mode: "drag",
    };
  };

  const startResize = (dir: ResizeDir) => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRect: rect,
      mode: "resize",
      dir,
    };
  };

  if (!open) return null;

  const HANDLE = 12; // px
  const EDGE = 10;

  const cornerSx = {
    width: HANDLE,
    height: HANDLE,
    position: "absolute" as const,
    background:
      "linear-gradient(135deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.22) 45%, rgba(0,0,0,0.22) 60%, rgba(0,0,0,0) 60%)",
    borderRadius: 0.75,
    opacity: 0.55,
  };

  const edgeSx = {
    position: "absolute" as const,
    background: "rgba(0,0,0,0.08)",
    opacity: 0.0, // invisible but grabbable; bump to 0.15 if you want to see it
  };

  return (
    <Box
      className="floating-panel"
      data-demo={props.dataDemo}
      sx={{
        position: "fixed",
        zIndex: 5000,
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 16,
        bgcolor: "rgba(255,255,255,0.98)",
        color: "black", // ✅ force text
        // "& *": { color: "black" },     // ✅ force children + icons
        border: "1px solid rgba(0,0,0,0.14)",
        //border: "2px solid black !important",

        display: "flex",
        flexDirection: "column",

        // avoid any accidental focus ring on the panel itself
        outline: "none",
        "&:focus, &:focus-visible": { outline: "none" },
        // ✅ kill Chrome focus ring on the internal SVG / wrappers
        "& .recharts-wrapper:focus, & .recharts-wrapper:focus-visible": {
          outline: "none !important",
        },
        "& .recharts-surface:focus, & .recharts-surface:focus-visible": {
          outline: "none !important",
        },
        "& svg:focus, & svg:focus-visible, & svg g:focus": {
          outline: "none !important",
        },
      }}
      onPointerDown={(e: any) => e.stopPropagation()}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.10)",
          userSelect: "none",
          color: "black",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.04), rgba(0,0,0,0.02))",
        }}
      >
        {/* Drag handle only on the title strip */}
        <Box
          onPointerDown={startDrag}
          sx={{ cursor: "move", flex: "1 1 auto", pr: 1 }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
            {title}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onPointerDown={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e: any) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: "1 1 auto", minHeight: 0, p: 0, position: "relative" }}>
        {children}
      </Box>

      {/* Resize handles: corners */}
      <Box
        onPointerDown={startResize("nw")}
        sx={{ ...cornerSx, left: 4, top: 4, cursor: "nwse-resize" }}
      />
      <Box
        onPointerDown={startResize("ne")}
        sx={{ ...cornerSx, right: 4, top: 4, cursor: "nesw-resize" }}
      />
      <Box
        onPointerDown={startResize("sw")}
        sx={{ ...cornerSx, left: 4, bottom: 4, cursor: "nesw-resize" }}
      />
      <Box
        onPointerDown={startResize("se")}
        sx={{ ...cornerSx, right: 4, bottom: 4, cursor: "nwse-resize" }}
      />

      {/* Resize handles: edges (invisible hit targets) */}
      <Box
        onPointerDown={startResize("n")}
        sx={{
          ...edgeSx,
          left: EDGE,
          right: EDGE,
          top: 0,
          height: EDGE,
          cursor: "ns-resize",
        }}
      />
      <Box
        onPointerDown={startResize("s")}
        sx={{
          ...edgeSx,
          left: EDGE,
          right: EDGE,
          bottom: 0,
          height: EDGE,
          cursor: "ns-resize",
        }}
      />
      <Box
        onPointerDown={startResize("w")}
        sx={{
          ...edgeSx,
          top: EDGE,
          bottom: EDGE,
          left: 0,
          width: EDGE,
          cursor: "ew-resize",
        }}
      />
      <Box
        onPointerDown={startResize("e")}
        sx={{
          ...edgeSx,
          top: EDGE,
          bottom: EDGE,
          right: 0,
          width: EDGE,
          cursor: "ew-resize",
        }}
      />
    </Box>
  );
}

type ThirdShotHoverTarget =
  | { kind: "point"; rallyIndex: number }
  | {
      kind: "stat";
      team: Team01;
      playerId: number | null;
      shotCategory: ShotCategoryFilter;
      metric?:
        | "attempts"
        | "successes"
        | "rallyWins"
        | "rallyLosses"
        | "errors"
        | "quality";
    };

type ShotCategoryFilter = "all" | "drive" | "drop";

function deriveShotCategory(
  shot: any,
): Exclude<ShotCategoryFilter, "all"> | null {
  const raw = [
    shot?.label,
    shot?.rawShot?.label,
    shot?.rawShot?.shot_type,
    shot?.rawShot?.shotType,
    shot?.shot_type,
    shot?.shotType,
    shot?.type,
    shot?.rawShot?.type,
  ]
    .filter((v) => typeof v === "string")
    .join(" ")
    .toLowerCase();

  if (raw.includes("drive")) return "drive";
  if (raw.includes("drop")) return "drop";
  return null;
}

function countPct(n: number, d: number): string {
  if (!d) return "0 (—)";
  return `${n} (${Math.round((n / d) * 100)}%)`;
}

function avgQuality(s: ShotOutcomeStats): string {
  if (!s.qualityCount) return "—";
  return (s.qualitySum / s.qualityCount).toFixed(2);
}

type ShotOutcomeStats = {
  attempts: number;
  rallyWins: number;
  rallyLosses: number;
  unknown: number;
  errors: number; // lost on 3rd shot
  qualitySum: number;
  qualityCount: number;
};

type PlayerStatRow = {
  playerId: number;
  total: ShotOutcomeStats;
  drive: ShotOutcomeStats;
  drop: ShotOutcomeStats;
  other: ShotOutcomeStats;
};

// type PlayerStatRow = {
//   playerId: number;
//   total: number;
//   win: number;
//   loss: number;
//   unk: number;
//   drives: number;
//   drops: number;
//   other: number;
// };

const COURT_WIDTH = 20;
const COURT_LENGTH = 44;

export type PlotPoint = ThirdShotPoint & {
  plotX: number; // feet
  plotY: number; // feet
};

export function toPlotXY(points: ThirdShotPoint[]): PlotPoint[] {
  // Only keep points with real court coords

  //   console.error("points", points);
  return points
    .filter((p) => Number.isFinite(p.courtX) && Number.isFinite(p.courtY))
    .map((p) => ({
      ...p,
      plotX: p.courtX as number,
      plotY: p.courtY as number,
    }));
}

const PAD = 0.06;

// function statsByPlayer(points: PlotPoint[], team: 0 | 1): PlayerStatRow[] {
//   const m = new Map<number, PlayerStatRow>();

//   for (const p of points) {
//     if (p.hitterTeam !== team) continue;
//     const pid = p.hitterPlayerId;
//     if (typeof pid !== "number") continue;

//     let row = m.get(pid);
//     if (!row) {
//       row = { playerId: pid, total: 0, win: 0, loss: 0, unk: 0 };
//       m.set(pid, row);
//     }

//     row.total += 1;
//     if (p.wonRally === true) row.win += 1;
//     else if (p.wonRally === false) row.loss += 1;
//     else row.unk += 1;
//   }

//   return Array.from(m.values()).sort((a, b) => a.playerId - b.playerId);
// }

function emptyShotOutcomeStats(): ShotOutcomeStats {
  return {
    attempts: 0,
    rallyWins: 0,
    rallyLosses: 0,
    unknown: 0,
    errors: 0,
    qualitySum: 0,
    qualityCount: 0,
  };
}

function pct(n: number, d: number): string {
  if (!d) return "—";
  return `${Math.round((n / d) * 100)}%`;
}

function addPointToStats(s: ShotOutcomeStats, p: PlotPoint) {
  s.attempts += 1;

  if (p.wonRally === true) s.rallyWins += 1;
  else if (p.wonRally === false) s.rallyLosses += 1;
  else s.unknown += 1;

  if (p.lostOnThirdShot) s.errors += 1;

  if (typeof p.shotQuality === "number" && Number.isFinite(p.shotQuality)) {
    s.qualitySum += p.shotQuality;
    s.qualityCount += 1;
  }
}
function statsByPlayer(points: PlotPoint[], team: 0 | 1): PlayerStatRow[] {
  const m = new Map<number, PlayerStatRow>();

  for (const p of points) {
    if (p.hitterTeam !== team) continue;

    const pid = p.hitterPlayerId;
    if (typeof pid !== "number") continue;

    let row = m.get(pid);
    if (!row) {
      row = {
        playerId: pid,
        total: emptyShotOutcomeStats(),
        drive: emptyShotOutcomeStats(),
        drop: emptyShotOutcomeStats(),
        other: emptyShotOutcomeStats(),
      };
      m.set(pid, row);
    }

    addPointToStats(row.total, p);

    if (p.shotCategory === "drive") addPointToStats(row.drive, p);
    else if (p.shotCategory === "drop") addPointToStats(row.drop, p);
    else addPointToStats(row.other, p);
  }

  return Array.from(m.values()).sort((a, b) => a.playerId - b.playerId);
}

// function statsByPlayer(points: PlotPoint[], team: 0 | 1): PlayerStatRow[] {
//   const m = new Map<number, PlayerStatRow>();

//   for (const p of points) {
//     if (p.hitterTeam !== team) continue;
//     const pid = p.hitterPlayerId;
//     if (typeof pid !== "number") continue;

//     let row = m.get(pid);
//     if (!row) {
//       row = {
//         playerId: pid,
//         total: 0,
//         win: 0,
//         loss: 0,
//         unk: 0,
//         drives: 0,
//         drops: 0,
//         other: 0,
//       };
//       m.set(pid, row);
//     }

//     row.total += 1;

//     if (p.wonRally === true) row.win += 1;
//     else if (p.wonRally === false) row.loss += 1;
//     else row.unk += 1;

//     if (p.shotCategory === "drive") row.drives += 1;
//     else if (p.shotCategory === "drop") row.drops += 1;
//     else row.other += 1;
//   }

//   return Array.from(m.values()).sort((a, b) => a.playerId - b.playerId);
// }
export type ThirdShotPoint = {
  rallyIndex: number;
  pointKey: string | number | null;

  hitterTeam: Team01;
  hitterPlayerId: number | null;

  wonRally: boolean | null; // hitterTeam won?
  // NEW: did hitter fault on the 3rd shot (i.e., point lost on shot 4)?
  lostOnThirdShot: boolean;

  // prefer court coords:
  courtX?: number;
  courtY?: number;

  player_positions: Vec2[] | undefined;
  shooter_movement_from_last_shot: Vec2 | undefined;
  // fallback overlay coords:
  u?: number;
  v?: number;

  nextCourtX?: number;
  nextCourtY?: number;
  shotCategory: Exclude<ShotCategoryFilter, "all"> | null;
  shotWeight: number;
  shotQuality?: number | null;
};

function PlayerFilter(props: {
  team: 0 | 1;
  teamColor: (team: 0 | 1, alpha?: number) => string;
  availablePlayerIds: number[];
  selectedPlayerId: number | null;
  onSelect: (pid: number | null) => void;
  renderPlayerIdentity: RenderPlayerIdentity;
}) {
  const {
    team,
    teamColor,
    availablePlayerIds,
    selectedPlayerId,
    onSelect,
    renderPlayerIdentity,
  } = props;

  if (!availablePlayerIds.length) return null;

  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography variant="body2" sx={{ fontWeight: 800, opacity: 0.8 }}>
        Players
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
        <Box
          data-happy-path="shot-location-player-filter-all"
          data-selected={selectedPlayerId == null ? "true" : "false"}
          onClick={() => onSelect(null)}
          // sx={{
          //   cursor: "pointer",
          //   userSelect: "none",
          //   px: 1,
          //   py: 0.4,
          //   borderRadius: 999,
          //   fontSize: 12,
          //   fontWeight: 900,
          //   border: "1px solid rgba(0,0,0,0.18)",
          //   bgcolor:
          //     selectedPlayerId == null
          //       ? "rgba(0,0,0,0.08)"
          //       : "rgba(0,0,0,0.02)",
          // }}
          sx={{
            height: 34,
            px: 1,
            borderRadius: "8px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            userSelect: "none",
            fontWeight: 900,
            fontSize: 13,
            border: "1px solid transparent",
            bgcolor: selectedPlayerId == null ? "rgba(0,0,0,0.07)" : "white",
            boxShadow:
              selectedPlayerId == null
                ? "inset 0 0 0 1px rgba(0,0,0,0.20)"
                : "none",
            "&:hover": {
              borderColor: "rgba(0,0,0,0.18)",
              bgcolor:
                selectedPlayerId == null
                  ? "rgba(0,0,0,0.10)"
                  : "rgba(0,0,0,0.03)",
            },
          }}
        >
          All
        </Box>

        {availablePlayerIds.map((pid) => {
          const selected = selectedPlayerId === pid;
          return (
            <Box
              key={pid}
              data-happy-path="shot-location-player-filter"
              data-shot-location-team={team}
              data-shot-location-player-id={pid}
              data-selected={selectedPlayerId === pid ? "true" : "false"}
              onClick={() => onSelect(pid)}
              sx={{
                minWidth: 0,
                height: 34,
                px: 0.75,
                borderRadius: "2px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                userSelect: "none",
                border: "1px solid transparent",
                bgcolor:
                  selectedPlayerId === pid ? teamColor(team, 0.16) : "white",
                boxShadow:
                  selectedPlayerId === pid
                    ? `inset 0 0 0 1px ${teamColor(team, 0.5)}`
                    : "none",
                "&:hover": {
                  borderColor: teamColor(team, 0.35),
                  bgcolor:
                    selectedPlayerId === pid
                      ? teamColor(team, 0.22)
                      : "rgba(0,0,0,0.03)",
                },
              }}
              // sx={{
              //   cursor: "pointer",
              //   userSelect: "none",
              //   px: 1,
              //   py: 0.4,
              //   borderRadius: 999,
              //   fontSize: 12,
              //   fontWeight: 900,
              //   border: selected
              //     ? `1px solid ${teamColor(team, 0.85)}`
              //     : "1px solid rgba(0,0,0,0.18)",
              //   bgcolor: selected ? teamColor(team, 0.2) : "rgba(0,0,0,0.02)",
              // }}
            >
              {renderPlayerIdentity
                ? renderPlayerIdentity({
                    playerId: pid,
                    labelOnly: false,
                    size: 34,
                    borderRadius: 5,
                    fontSize: 12,
                  })
                : `p${pid}`}{" "}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function formatShotQuality(q: number | null | undefined): string {
  if (typeof q !== "number" || !Number.isFinite(q)) return "Q: —";
  return `Q: ${q.toFixed(2)}`;
}

export function extractThirdShotPoints(args: {
  rows: KitchenArrivalRow[];
  playerIdToTeam: (playerId: number | null) => Team01 | null;
}): ThirdShotPoint[] {
  const { rows, playerIdToTeam } = args;
  const out: ThirdShotPoint[] = [];

  for (const r of rows) {
    // console.error(r.shotNumberOverlays)

    const ov4 = r.shotNumberOverlays?.find((s) => s.shotNumber === 3);
    if (!ov4) continue;

    const hitterTeam = playerIdToTeam(ov4.playerId);
    if (hitterTeam == null) continue;

    const loc = ov4.trajectoryStart?.location ?? null;
    const nextShot = r.shotNumberOverlays?.find((s) => s.shotNumber === 4);
    const nextLoc = nextShot?.trajectoryStart?.location ?? null;
    const shotCategory = deriveShotCategory(ov4);

    //const faultType = (ov4 as any)?.faultType ?? null;

    // const endedOnThisShot =
    //   r.shotNumberOverlays?.length === 3 &&
    //   ((ov4 as any)?.faultType || ov4?.label?.includes("fault")); // || (ov4 as any)?.winnerType);

    // if (r.shotNumberOverlays?.length === 3) {
    //   console.error("3rd shot overlay - label", ov4);
    // }

    // const lostOnThirdShot =
    //   endedOnThisShot && r.winnerTeam != null && r.winnerTeam !== hitterTeam;

    // IMPORTANT: detecting "ended on 3rd" is trickier than 4th.
    // Some feeds don't mark a 3rd-shot miss as "fault"; instead, the rally simply ends
    // with max shotNumber == 3.
    const hasLaterShot = (r.shotNumberOverlays ?? []).some(
      (s: any) => typeof s?.shotNumber === "number" && s.shotNumber > 3,
    );

    const endedOnThisShot = !hasLaterShot && r.winnerTeam != null;

    const lostOnThirdShot = endedOnThisShot && r.winnerTeam !== hitterTeam;

    const shotQualityParts = getOverlayShotQualityParts(ov4);

    const shotQuality = shotQualityParts.overall;

    out.push({
      rallyIndex: r.rallyIndex,
      pointKey: r.pointKey,
      hitterTeam,
      hitterPlayerId:
        typeof (ov4 as any)?.playerId === "number"
          ? (ov4 as any).playerId
          : null,
      wonRally: r.winnerTeam == null ? null : r.winnerTeam === hitterTeam,
      lostOnThirdShot: lostOnThirdShot,
      courtX: (loc as any)?.x ?? undefined,
      courtY: (loc as any)?.y ?? undefined,
      u: ov4.u,
      v: ov4.v,
      player_positions: ov4.player_positions || undefined,
      shooter_movement_from_last_shot:
        ov4.shooter_movement_from_last_shot || undefined,
      nextCourtX: (nextLoc as any)?.x ?? undefined,
      nextCourtY: (nextLoc as any)?.y ?? undefined,
      shotCategory,
      shotWeight: shotCategory === "drive" ? 1.35 : 1,
      shotQuality,
    });
  }

  return out;
}

function pointMatchesHoverTarget(
  p: PlotPoint,
  target: ThirdShotHoverTarget | null,
): boolean {
  if (!target) return true;

  if (target.kind === "point") {
    return p.rallyIndex === target.rallyIndex;
  }

  if (p.hitterTeam !== target.team) return false;

  if (target.playerId != null && p.hitterPlayerId !== target.playerId) {
    return false;
  }

  if (target.shotCategory !== "all" && p.shotCategory !== target.shotCategory) {
    return false;
  }

  if (target.metric === "successes") return !p.lostOnThirdShot;

  if (target.metric === "rallyWins") return p.wonRally === true;
  if (target.metric === "rallyLosses") return p.wonRally === false;
  if (target.metric === "errors") return p.lostOnThirdShot;

  return true;
}

function ThirdShotTargetTitle(props: {
  target: ThirdShotHoverTarget | null;
  points: PlotPoint[];
  teamColor: (team: 0 | 1, alpha?: number) => string;
  renderPlayerIdentity: RenderPlayerIdentity;
}) {
  const { target, teamColor, renderPlayerIdentity, points } = props;

  if (!target) {
    return <span>Showing all 3rd-shot contact locations</span>;
  }

  if (target.kind === "point") {
    return <span>Selected rally point</span>;
  }

  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const pluralize = (count: number, label: string) => {
    if (count !== 1) return label;
    if (label === "attempts") return "attempt";
    if (label === "successes") return "success";
    if (label === "rally wins") return "rally win";
    if (label === "rally losses") return "rally loss";
    if (label === "3rd-shot errors") return "3rd-shot error";
    return label;
  };

  const matchingCount =
    target.kind === "stat"
      ? points.filter((p) => pointMatchesHoverTarget(p, target)).length
      : null;

  // const typeLabel =
  //   target.shotCategory === "all"
  //     ? "all 3rd shots"
  //     : `${target.shotCategory} 3rd shots`;

  const typeLabel =
    target.shotCategory === "all"
      ? "All 3rd shots"
      : `${cap(target.shotCategory)} 3rd shots`;

  const metricLabel =
    target.metric === "attempts"
      ? "attempts"
      : target.metric === "successes"
        ? "successes"
        : target.metric === "rallyWins"
          ? "rally wins"
          : target.metric === "rallyLosses"
            ? "rally losses"
            : target.metric === "errors"
              ? "3rd-shot errors"
              : target.metric === "quality"
                ? "shot quality"
                : "selected shots";

  const matchingPct =
    target.kind === "stat" &&
    matchingCount != null &&
    target.metric != null &&
    target.metric !== "attempts"
      ? ` (${pct(
          matchingCount,
          points.filter(
            (p) =>
              p.hitterTeam === target.team &&
              (target.playerId == null ||
                p.hitterPlayerId === target.playerId) &&
              (target.shotCategory === "all" ||
                p.shotCategory === target.shotCategory),
          ).length,
        )})`
      : "";

  const shortTypeLabel =
    target.shotCategory === "all" ? "All" : cap(target.shotCategory);

  const statText =
    matchingCount != null
      ? `${matchingCount}${matchingPct} ${pluralize(matchingCount, metricLabel)}`
      : metricLabel;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        minWidth: 0,
      }}
    >
      {target.playerId != null ? (
        renderPlayerIdentity({
          playerId: target.playerId,
          labelOnly: false,
          size: 24,
          borderRadius: 4,
          fontSize: 11,
        })
      ) : (
        <span>
          Team {target.team} {target.team === 0 ? "far" : "near"}
        </span>
      )}

      <span>
        · {shortTypeLabel} 3rds · {statText}
      </span>
    </Box>

    // <Box
    //   sx={{
    //     display: "inline-flex",
    //     alignItems: "center",
    //     justifyContent: "center",
    //     gap: 0.5,
    //   }}
    // >
    //   <span>
    //     Team {target.team} ({target.team === 0 ? "far" : "near"})
    //   </span>

    //   {target.playerId != null && (
    //     <>
    //       <span>·</span>
    //       {renderPlayerIdentity({
    //         playerId: target.playerId,
    //         labelOnly: false,
    //         size: 24,
    //         borderRadius: 4,
    //         fontSize: 11,
    //       })}
    //     </>
    //   )}

    //   {target.playerId == null && <span>· all players</span>}

    //   <span>
    //     · {typeLabel} ·{" "}
    //     {matchingCount != null
    //       ? `${matchingCount}${matchingPct} ${pluralize(matchingCount, metricLabel)}`
    //       : metricLabel}
    //   </span>
    // </Box>
  );
}
export function ThirdShotLocationsPopup(props: {
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity: RenderPlayerIdentity;

  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;

  packageId: string;
  rows: KitchenArrivalRow[];
  playerIdToTeam: (playerId: number | null) => 0 | 1 | null;
  teamColor: (team: 0 | 1, alpha?: number) => string;
  popupW_VideoPart: string | number; // e.g. "92vw" or 980
  popupH_VideoPart: string | number; // e.g. "60vh" or 520
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const [mainRect, setMainRect] = React.useState<FloatingRect>(() => ({
    x: 24,
    y: 24,
    w: 760,
    h: 540,
  }));

  const [hoverTarget, setHoverTarget] =
    React.useState<ThirdShotHoverTarget | null>(null);

  const [pinnedTarget, setPinnedTarget] =
    React.useState<ThirdShotHoverTarget | null>(null);

  const activeTarget = pinnedTarget ?? hoverTarget;
  //const activeTarget = hoverTarget ?? pinnedTarget;

  const didCenterMainRef = React.useRef(false);

  const [videoRect, setVideoRect] = React.useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  }>(() => ({
    x: 24,
    y: 24,
    w: 720,
    h: 450,
  }));

  const {
    open,
    anchorEl,
    onClose,
    packageId,
    rows,
    playerIdToTeam,
    teamColor,
    popupW_VideoPart,
    popupH_VideoPart,
    firefightsClips,
    firefightRallySet,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
  } = props;

  const [showMovement, setShowMovement] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement | null>(null);

  const [videoRallyIndex, setVideoRallyIndex] = React.useState<number | null>(
    null,
  );

  const rowByRallyIndex = React.useMemo(() => {
    const m = new Map<number, KitchenArrivalRow>();
    for (const r of rows) m.set(r.rallyIndex, r);
    return m;
  }, [rows]);

  const videoRow = React.useMemo(() => {
    if (videoRallyIndex == null) return null;
    return rowByRallyIndex.get(videoRallyIndex) ?? null;
  }, [rowByRallyIndex, videoRallyIndex]);

  React.useEffect(() => {
    if (!open) {
      didCenterMainRef.current = false;
      return;
    }
    if (didCenterMainRef.current) return;

    const w = mainRect.w;
    const h = mainRect.h;

    const x = Math.max(8, Math.floor((window.innerWidth - w) / 2));
    const y = Math.max(8, Math.floor((window.innerHeight - h) / 2));

    setMainRect({ x, y, w, h });
    didCenterMainRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openVideoForPoint = React.useCallback(
    (p: PlotPoint, clientX: number, clientY: number) => {
      setVideoRallyIndex(p.rallyIndex);

      const w = videoRect.w ?? 420;
      const h = videoRect.h ?? 300;

      const x = Math.max(8, Math.floor((window.innerWidth - w) / 2));
      const y = Math.max(8, Math.floor((window.innerHeight - h) / 2));

      // keep it on-screen (optional extra clamp)
      const x2 = Math.min(window.innerWidth - w - 8, x);
      const y2 = Math.min(window.innerHeight - h - 8, y);

      setVideoRect((r) => ({ ...r, x: x2, y: y2, w, h }));
    },
    [videoRect.w, videoRect.h],
  );

  //   console.error(
  //     "3rd shots points from backend",
  //     rows.map((r) => r.shotNumberOverlays![3]),
  //   );

  const points = React.useMemo(() => {
    const raw = extractThirdShotPoints({ rows, playerIdToTeam });
    const pt2 = toPlotXY(raw);
    // console.error("3rd shot points after processing", pt2);
    return pt2;
  }, [rows, playerIdToTeam]);

  const playerIdsByTeam = React.useMemo(() => {
    const by: Record<Team01, number[]> = { 0: [], 1: [] };
    const seen0 = new Set<number>();
    const seen1 = new Set<number>();

    for (const p of points) {
      const pid = p.hitterPlayerId;
      if (typeof pid !== "number") continue;
      if (p.hitterTeam === 0) {
        if (!seen0.has(pid)) {
          seen0.add(pid);
          by[0].push(pid);
        }
      } else {
        if (!seen1.has(pid)) {
          seen1.add(pid);
          by[1].push(pid);
        }
      }
    }

    by[0].sort((a, b) => a - b);
    by[1].sort((a, b) => a - b);
    return by;
  }, [points]);

  const [playerFilterT0, setPlayerFilterT0] = React.useState<number | null>(
    null,
  );
  const [playerFilterT1, setPlayerFilterT1] = React.useState<number | null>(
    null,
  );
  const [shotCategoryFilter, setShotCategoryFilter] =
    React.useState<ShotCategoryFilter>("all");

  // quick sanity toggles for “is the horizontal mapping mirrored?”
  const [flipX, setFlipX] = React.useState(false);

  const activeVideoRow = React.useMemo(() => {
    if (videoRallyIndex == null) return null;
    return rows.find((r) => r.rallyIndex === videoRallyIndex) ?? null;
  }, [rows, videoRallyIndex]);

  const videoPoint = React.useMemo(() => {
    if (videoRallyIndex == null) return null;
    // use `points` (not filteredPoints) so it still works even if filters change
    return points.find((p) => p.rallyIndex === videoRallyIndex) ?? null;
  }, [videoRallyIndex, points]);

  const closeVideo = React.useCallback(() => setVideoRallyIndex(null), []);

  // reset filters if those playerIds disappear (new dataset / new package)
  React.useEffect(() => {
    if (
      playerFilterT0 != null &&
      !playerIdsByTeam[0].includes(playerFilterT0)
    ) {
      setPlayerFilterT0(null);
    }
    if (
      playerFilterT1 != null &&
      !playerIdsByTeam[1].includes(playerFilterT1)
    ) {
      setPlayerFilterT1(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerIdsByTeam[0].join(","), playerIdsByTeam[1].join(",")]);

  const filteredPoints = React.useMemo(() => {
    const activeSelectedTeam: Team01 | null =
      playerFilterT0 != null ? 0 : playerFilterT1 != null ? 1 : null;

    const base = points.filter((p) => {
      if (activeSelectedTeam != null && p.hitterTeam !== activeSelectedTeam) {
        return false;
      }
      if (
        p.hitterTeam === 0 &&
        playerFilterT0 != null &&
        p.hitterPlayerId !== playerFilterT0
      )
        return false;
      if (
        p.hitterTeam === 1 &&
        playerFilterT1 != null &&
        p.hitterPlayerId !== playerFilterT1
      )
        return false;
      if (shotCategoryFilter !== "all" && p.shotCategory !== shotCategoryFilter)
        return false;
      return true;
    });

    // const chartDrilldownTitle = (
    //   <ThirdShotTargetTitle
    //     target={activeTarget}
    //     points={filteredPoints}
    //     renderPlayerIdentity={renderPlayerIdentity}
    //   />
    // );
    //  thirdShotTargetTitle(activeTarget);

    if (!flipX) return base;

    // return base.map((p) => ({
    //   ...p,
    //   plotU: 1 - p.plotU,
    // }));

    return base.map((p) => ({
      ...p,
      plotX: COURT_WIDTH - p.plotX,
    }));
    // return points.filter((p) => {
    //   if (p.hitterTeam === 0 && playerFilterT0 != null) {
    //     return p.hitterPlayerId === playerFilterT0;
    //   }
    //   if (p.hitterTeam === 1 && playerFilterT1 != null) {
    //     return p.hitterPlayerId === playerFilterT1;
    //   }
    //   return true;
    // });
  }, [points, playerFilterT0, playerFilterT1, shotCategoryFilter, flipX]);

  const t0PlayerStats = React.useMemo(() => statsByPlayer(points, 0), [points]);
  const t1PlayerStats = React.useMemo(() => statsByPlayer(points, 1), [points]);

  // ---------------------------
  // Hover preview (non-pinned)
  // ---------------------------
  const [hoverRallyIndex, setHoverRallyIndex] = React.useState<number | null>(
    null,
  );
  const [hoverClientPos, setHoverClientPos] = React.useState<{
    x: number;
    y: number;
  } | null>(null);

  const hoverOpenTimerRef = React.useRef<number | null>(null);
  const hoverCloseTimerRef = React.useRef<number | null>(null);

  const [isHoveringPreviewPanel, setIsHoveringPreviewPanel] =
    React.useState(false);

  const HOVER_OPEN_MS = 140;
  const HOVER_CLOSE_MS = 220;

  const clearHoverTimers = React.useCallback(() => {
    if (hoverOpenTimerRef.current != null) {
      window.clearTimeout(hoverOpenTimerRef.current);
      hoverOpenTimerRef.current = null;
    }
    if (hoverCloseTimerRef.current != null) {
      window.clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => clearHoverTimers();
  }, [clearHoverTimers]);

  const openHoverPreview = React.useCallback(
    (p: PlotPoint, clientX: number, clientY: number) => {
      // if a pinned popup is open for this rally already, don’t bother previewing
      if (videoRallyIndex != null && videoRallyIndex === p.rallyIndex) return;

      // ✅ cancel any pending close (THIS IS IMPORTANT)
      if (hoverCloseTimerRef.current != null) {
        window.clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }

      setHoverClientPos({ x: clientX, y: clientY });

      // debounce open
      if (hoverOpenTimerRef.current != null)
        window.clearTimeout(hoverOpenTimerRef.current);

      hoverOpenTimerRef.current = window.setTimeout(() => {
        setHoverRallyIndex(p.rallyIndex);
      }, HOVER_OPEN_MS);
    },
    [videoRallyIndex],
  );

  const closeHoverPreviewSoon = React.useCallback(() => {
    // ✅ IMPORTANT: if we’re closing, cancel any pending open
    if (hoverOpenTimerRef.current != null) {
      window.clearTimeout(hoverOpenTimerRef.current);
      hoverOpenTimerRef.current = null;
    }

    if (hoverCloseTimerRef.current != null)
      window.clearTimeout(hoverCloseTimerRef.current);

    hoverCloseTimerRef.current = window.setTimeout(() => {
      if (isHoveringPreviewPanel) return;
      setHoverRallyIndex(null);
      setHoverClientPos(null);
    }, HOVER_CLOSE_MS);
  }, [isHoveringPreviewPanel]);

  const updateHoverPos = React.useCallback(
    (clientX: number, clientY: number) => {
      setHoverClientPos({ x: clientX, y: clientY });
    },
    [],
  );

  const hoverRow = React.useMemo(() => {
    if (hoverRallyIndex == null) return null;
    return rowByRallyIndex.get(hoverRallyIndex) ?? null;
  }, [hoverRallyIndex, rowByRallyIndex]);

  const hoverPoint = React.useMemo(() => {
    if (!hoverRow) return null;
    return (
      (filteredPoints.find((p) => p.rallyIndex === hoverRow.rallyIndex) as
        | PlotPoint
        | undefined) ?? null
    );
  }, [hoverRow, filteredPoints]);

  //if (!open || !anchorEl) return null;
  if (!open) return null;

  const t0 = points.filter((p) => p.hitterTeam === 0);
  const t1 = points.filter((p) => p.hitterTeam === 1);

  // simple fixed popup positioning near anchor
  // const rect = anchorEl.getBoundingClientRect();
  // const left = Math.min(window.innerWidth - 560, Math.max(8, rect.left));
  // const top = Math.min(window.innerHeight - 520, Math.max(8, rect.bottom + 8));

  return (
    <>
      {/* <Box
        ref={popupRef}
        //   onClick={() => {
        //     setVideoRallyIndex(null);
        //     setVideoPos(null);
        //   }}
        sx={{
          position: "fixed",
          zIndex: 2500,
          left,
          top,
          width: 760, //popupW, // 760,
          height: 540, //popupH, // 520,
          bgcolor: "background.paper",
          border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: 2,
          boxShadow: 10,
          overflow: "hidden",
          color: "black",
        }}
      > */}

      <FloatingPanel
        open={open}
        rect={mainRect}
        onChangeRect={setMainRect}
        onClose={onClose}
        title="3rd Shot Contact Locations"
        minW={1320}
        minH={800}
        color="black"
      >
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* TOP (auto height) */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              flex: "0 0 auto",
              borderBottom: "1px solid rgba(0,0,0,0.10)",
            }}
          >
            {/* <Typography sx={{ fontWeight: 800 }}>3rd-shot Locations</Typography> */}
            <Typography sx={{ opacity: 0.95 }}>
              <span style={{ color: teamColor(0), fontWeight: 700 }}>
                Team 0
              </span>
              : {t0.length}
              &nbsp; &nbsp; &nbsp;
              <span style={{ color: teamColor(1), fontWeight: 700 }}>
                Team 1
              </span>
              : {t1.length}
              &nbsp; &nbsp; &nbsp;
            </Typography>
          </Box>
          {/* <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton> */}

          {/* BODY (fills remaining height) */}

          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              p: 1.5,
              display: "flex",
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            {/* LEFT: summaries + per-team player filters */}
            <Box sx={{ width: 450, flex: "0 0 auto", overflowY: "auto" }}>
              <Box
                sx={{
                  mb: 0.75,
                  px: 0.5,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "black",
                }}
              >
                Hover table values to preview chart shots. Click a value to keep that filter active
(hover preview pauses while active). Click a player avatar to isolate that player.
Use Clear to reset.
              </Box>

              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.2)",
                  borderRadius: "2px",
                  paddingLeft: "2px",
                  paddingBottom: "2px",
                }}
              >
                {/* <PlayerFilter
                  team={0}
                  teamColor={teamColor}
                  availablePlayerIds={playerIdsByTeam[0]}
                  selectedPlayerId={playerFilterT0}
                  onSelect={(pid) => {
                    setPlayerFilterT0(pid);
                    setPlayerFilterT1(null);
                  }}
                  renderPlayerIdentity={renderPlayerIdentity}
                  //hoverTarget={hoverTarget}
                  //onHoverTarget={setHoverTarget}
                /> */}
                <TeamSummary
                  title="Team 0"
                  points={t0}
                  team={0}
                  teamColor={teamColor}
                  playerStats={t0PlayerStats}
                  selectedPlayerId={playerFilterT0}
                  onSelectPlayer={(pid) => {
                    setHoverTarget(null);

                    setPlayerFilterT0((prev) => {
                      const next = prev === pid ? null : pid;

                      setPinnedTarget(
                        next == null
                          ? null
                          : {
                              kind: "stat",
                              team: 0,
                              playerId: next,
                              shotCategory: "all",
                              metric: "attempts",
                            },
                      );

                      return next;
                    });

                    setPlayerFilterT1(null);
                  }}
                  // onSelectPlayer={(pid) => {
                  //   setPinnedTarget(null);
                  //   setHoverTarget(null);
                  //   setPlayerFilterT0((prev) => (prev === pid ? null : pid));
                  //   setPlayerFilterT1(null);
                  // }}
                  renderPlayerIdentity={renderPlayerIdentity}
                  hoverTarget={activeTarget} // hoverTarget}
                  onHoverTarget={setHoverTarget}
                  pinnedTarget={pinnedTarget}
                  onPinnedTarget={setPinnedTarget}
                  onActivateTarget={(target) => {
                    if (target.kind !== "stat") return;

                    setHoverTarget(null);

                    if (target.team === 0) {
                      setPlayerFilterT0(target.playerId);
                      setPlayerFilterT1(null);
                    } else {
                      setPlayerFilterT1(target.playerId);
                      setPlayerFilterT0(null);
                    }
                  }}
                />
              </Box>

              <Box sx={{ height: 12 }} />

              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.2)",
                  borderRadius: "2px",
                  paddingLeft: "2px",
                  paddingBottom: "2px",
                }}
              >
                {/* <PlayerFilter
                  team={1}
                  teamColor={teamColor}
                  availablePlayerIds={playerIdsByTeam[1]}
                  selectedPlayerId={playerFilterT1}
                  onSelect={(pid) => {
                    setPlayerFilterT1(pid);
                    setPlayerFilterT0(null);
                  }}
                  renderPlayerIdentity={renderPlayerIdentity}
                /> */}

                <TeamSummary
                  title="Team 1"
                  points={t1}
                  team={1}
                  teamColor={teamColor}
                  playerStats={t1PlayerStats}
                  selectedPlayerId={playerFilterT1}
                  onSelectPlayer={(pid) => {
                    setHoverTarget(null);

                    setPlayerFilterT1((prev) => {
                      const next = prev === pid ? null : pid;

                      setPinnedTarget(
                        next == null
                          ? null
                          : {
                              kind: "stat",
                              team: 1,
                              playerId: next,
                              shotCategory: "all",
                              metric: "attempts",
                            },
                      );

                      return next;
                    });

                    setPlayerFilterT0(null);
                  }}
                  // onSelectPlayer={(pid) => {
                  //   setPinnedTarget(null);
                  //   setHoverTarget(null);
                  //   setPlayerFilterT1((prev) => (prev === pid ? null : pid));
                  //   setPlayerFilterT0(null);
                  // }}
                  renderPlayerIdentity={renderPlayerIdentity}
                  // hoverTarget={hoverTarget}
                  hoverTarget={activeTarget} // hoverTarget}
                  onHoverTarget={setHoverTarget}
                  pinnedTarget={pinnedTarget}
                  onPinnedTarget={setPinnedTarget}
                  onActivateTarget={(target) => {
                    if (target.kind !== "stat") return;

                    setHoverTarget(null);

                    if (target.team === 0) {
                      setPlayerFilterT0(target.playerId);
                      setPlayerFilterT1(null);
                    } else {
                      setPlayerFilterT1(target.playerId);
                      setPlayerFilterT0(null);
                    }
                  }}
                />
              </Box>
            </Box>

            {/* MIDDLE: chart */}
            <Box
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                minHeight: 0,
                display: "flex",
              }}
            >
              <Box
                sx={{
                  // border: "1px solid rgba(0,0,0,0.2)",
                  flex: "1 1 auto",
                  minHeight: 0,
                  minWidth: 0,
                  borderRadius: "2px",
                  paddingLeft: "2px",
                  paddingBottom: "2px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    minHeight: 32,
                    px: 0.25,
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    alignItems: "center",
                    columnGap: 1,
                  }}
                >
                  <Box
                    onClick={() => {
                      setPinnedTarget(null);
                      setHoverTarget(null);
                      setPlayerFilterT0(null);
                      setPlayerFilterT1(null);
                      setShotCategoryFilter("all");
                    }}
                    sx={{
                      px: 1.25,
                      height: 28,
                      borderRadius: "8px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontWeight: 900,
                      fontSize: 12,
                      bgcolor: "rgba(0,0,0,0.08)",
                      boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.22)",
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.13)",
                      },
                    }}
                  >
                    Clear
                  </Box>

                  <Box
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 900,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <ThirdShotTargetTitle
                      target={activeTarget}
                      points={filteredPoints}
                      teamColor={teamColor}
                      renderPlayerIdentity={renderPlayerIdentity}
                    />
                  </Box>

                  <Box sx={{ width: 56 }} />
                </Box>

                <CourtScatterRecharts
                  points={filteredPoints}
                  teamColor={teamColor}
                  // hoverTarget={hoverTarget}
                  hoverTarget={activeTarget} // hoverTarget}
                  onPickPoint={(p, e) => {
                    clearHoverTimers();
                    setHoverRallyIndex(null);
                    setIsHoveringPreviewPanel(false);
                    setHoverClientPos(null);
                    openVideoForPoint(p, e.clientX, e.clientY);
                  }}
                  onHoverPoint={(phase, p, e) => {
                    if (phase === "enter") {
                      setHoverTarget({
                        kind: "point",
                        rallyIndex: p.rallyIndex,
                      });
                      openHoverPreview(p, e.clientX, e.clientY);
                    }

                    if (phase === "move") {
                      updateHoverPos(e.clientX, e.clientY);
                    }

                    if (phase === "leave") {
                      setHoverTarget(null);

                      if (hoverOpenTimerRef.current != null) {
                        window.clearTimeout(hoverOpenTimerRef.current);
                        hoverOpenTimerRef.current = null;
                      }

                      closeHoverPreviewSoon();
                    }
                  }}
                  onLeaveChart={() => {
                    setHoverTarget(null);

                    if (hoverOpenTimerRef.current != null) {
                      window.clearTimeout(hoverOpenTimerRef.current);
                      hoverOpenTimerRef.current = null;
                    }

                    closeHoverPreviewSoon();
                  }}
                  // optional: keep the preview pinned to cursor even when not over a dot
                  onMoveChart={(x, y) => {
                    if (hoverRallyIndex != null) updateHoverPos(x, y);
                  }}
                  shotCategoryFilter={shotCategoryFilter}
                />
              </Box>
            </Box>

            {/* RIGHT: legend */}
            {/* <Box sx={{ width: 200, flex: "0 0 auto", overflow: "auto" }}>
              <Legend teamColor={teamColor} />
              <Box
                sx={{ marginTop: "10px", fontStyle: "italic", fontSize: 12 }}
              >
                {"Hover on points to preview video, click to open pinned video"}
              </Box>
            </Box> */}
            <Box
              sx={{
                width: 320, // ✅ give it room (was 200)
                flex: "0 0 auto",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box sx={{ flex: "0 0 auto" }}>
                <Legend teamColor={teamColor} />
                <Box
                  sx={{
                    mt: 1,
                    fontStyle: "italic",
                    fontSize: 12,
                    opacity: 0.85,
                  }}
                >
                  Hover a point to preview. Click point to open in new popup.
                </Box>
              </Box>

              {/* ✅ Hover preview lives here */}
              <Box
                sx={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: hoverRow
                    ? "1px solid rgba(0,0,0,0.14)"
                    : "1px dashed rgba(0,0,0,0.10)",
                  bgcolor: "rgba(255,255,255,0.85)",
                  boxShadow: hoverRow ? 6 : 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {!hoverRow || videoRow != null ? (
                  <Box sx={{ p: 1, fontSize: 12, opacity: 0.65 }}>
                    {videoRow != null
                      ? "Pinned video is open."
                      : "Hover a dot to preview here."}
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(0,0,0,0.10)",
                        background:
                          "linear-gradient(to bottom, rgba(0,0,0,0.04), rgba(0,0,0,0.02))",
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, fontSize: 12 }}>
                        Rally {hoverRow.rallyIndex + 1}
                      </Typography>

                      {hoverPoint && (
                        <Box
                          sx={{
                            ml: 1,
                            px: 1,
                            py: 0.15,
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 900,
                            lineHeight: 1.3,
                            bgcolor: teamColor(hoverPoint.hitterTeam, 0.18),
                            border: `1px solid ${teamColor(hoverPoint.hitterTeam, 0.55)}`,
                            color: "rgba(0,0,0,0.92)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Team {hoverPoint.hitterTeam} (
                          {hoverPoint.hitterTeam === 0 ? "far" : "near"})
                          {hoverPoint.hitterPlayerId != null
                            ? ` · P${hoverPoint.hitterPlayerId}`
                            : ""}
                          {hoverPoint.wonRally == null
                            ? ""
                            : hoverPoint.wonRally
                              ? " · W"
                              : " · L"}
                        </Box>
                      )}
                      <Box
                        onClick={(e: any) => {
                          e.stopPropagation();
                          const pp = filteredPoints.find(
                            (p) => p.rallyIndex === hoverRow.rallyIndex,
                          );
                          if (pp) {
                            openVideoForPoint(pp as any, 0, 0);
                          }
                          setHoverRallyIndex(null);
                          setHoverClientPos(null);
                        }}
                        sx={{
                          cursor: "pointer",
                          userSelect: "none",
                          px: 1,
                          py: 0.25,
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 900,
                          // border: "1px solid rgba(0,0,0,0.18)",
                          // bgcolor: "rgba(0,0,0,0.04)",
                        }}
                      >
                        Click point to open in new popup
                      </Box>
                    </Box>

                    <Box
                      data-demo="shot-location-hover-preview"
                      sx={{ p: 0.75, flex: "1 1 auto", minHeight: 0 }}
                    >
                      <ActiveRallyVideo
                        insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                        renderPlayerIdentity={renderPlayerIdentity}
                        aiContextLabel="3rd Shots"
                        focusedFilter={null}
                        key={`hover-${hoverRow.rallyIndex}`}
                        controlsPlacement="left"
                        row={hoverRow}
                        packageId={packageId}
                        rows={rows}
                        activeShotNumber={3}
                        beginningScore={hoverRow.scoreBefore}
                        endingScore={hoverRow.scoreAfter}
                        showCondensedTimeline={false}
                        showPopoutButton={true}
                        fontColor={"black"}
                        firefightsClips={firefightsClips}
                        firefightRallySet={firefightRallySet}
                        showRateControls={false}
                        showScoreEtc={false}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </FloatingPanel>
      {/* </Box> */}

      {/* {hoverRow && hoverClientPos && videoRow == null && (
        <Box
          onMouseEnter={() => {
            setIsHoveringPreviewPanel(true);
            // cancel any pending close
            if (hoverCloseTimerRef.current != null) {
              window.clearTimeout(hoverCloseTimerRef.current);
              hoverCloseTimerRef.current = null;
            }
          }}
          onMouseLeave={() => {
            setIsHoveringPreviewPanel(false);
            closeHoverPreviewSoon();
          }}
          sx={() => {
            const W = 420;
            const H = 300;

            // position near cursor, clamped
            const pad = 12;
            let x = hoverClientPos.x + pad;
            let y = hoverClientPos.y + pad;

            x = Math.min(window.innerWidth - W - 8, Math.max(8, x));
            y = Math.min(window.innerHeight - H - 8, Math.max(8, y));

            return {
              position: "fixed",
              zIndex: 6500,
              left: x,
              top: y,
              width: W,
              height: H,
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 16,
              bgcolor: "rgba(255,255,255,0.98)",
              border: "1px solid rgba(0,0,0,0.14)",
              pointerEvents: "auto",
              color: "black",
            };
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.6,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(0,0,0,0.10)",
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.04), rgba(0,0,0,0.02))",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 12 }}>
                Rally {hoverRow.rallyIndex} · preview
              </Typography>

              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  // pin it:
                  openVideoForPoint(
                    {
                      ...(filteredPoints.find(
                        (pp) => pp.rallyIndex === hoverRow.rallyIndex,
                      ) as any),
                    },
                    hoverClientPos.x,
                    hoverClientPos.y,
                  );
                  // kill hover
                  setHoverRallyIndex(null);
                  setHoverClientPos(null);
                }}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 900,
                  border: "1px solid rgba(0,0,0,0.18)",
                  bgcolor: "rgba(0,0,0,0.04)",
                }}
              >
                Click to pin
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              p: 0.75,
              height: `calc(100% - 34px)`,
              zIndex: 7500,
              position: "relative",
            }}
          >
            <ActiveRallyVideo
              key={`hover-${hoverRow.rallyIndex}`} // ✅ forces a fresh subtree/video
              controlsPlacement="top"
              row={hoverRow}
              rows={rows}
              activeShotNumber={3}
              beginningScore={hoverRow.scoreBefore}
              endingScore={hoverRow.scoreAfter}
              showCondensedTimeline={false} // keep preview lighter
              fontColor={"black"}
              firefightsClips={firefightsClips}
              firefightRallySet={firefightRallySet}
              showRateControls={false} // hide rate controls in preview to keep it simpler
              showScoreEtc={false}
            />
          </Box>
        </Box>
      )} */}
      {videoRow && (
        <FloatingPanel
          open={videoRow != null}
          rect={videoRect}
          onChangeRect={setVideoRect}
          onClose={() => {
            setVideoRallyIndex(null);
          }}
          title={
            videoRow
              ? `Rally ${videoRow.rallyIndex + 1} · 3rd Shot Contact Location · Team ${videoPoint?.hitterTeam} `
              : "Video"
          }
          minW={1320}
          minH={620}
          color="black"
          dataDemo="shot-location-pinned-video"
        >
          {videoRow && (
            <ActiveRallyVideo
              aiContextLabel="3rd Shots"
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
              renderPlayerIdentity={renderPlayerIdentity}
              condensedTimelineLeftEl={
                videoPoint ? (
                  // <Box
                  //   sx={{
                  //     marginRight: "6px",
                  //     width: 100,
                  //     height: 100 /* tweak later */,
                  //   }}
                  // >
                  //   <CourtScatterSinglePoint
                  //     p={videoPoint}
                  //     teamColor={teamColor}
                  //   />
                  // </Box>

                  <Box
                    sx={{
                      marginRight: "6px",
                      width: 100,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                    }}
                  >
                    <Box
                      sx={{
                        height: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 800,
                        lineHeight: 1,
                        color: "rgba(0,0,0,0.78)",
                        mb: 0.25,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatShotQuality(videoPoint.shotQuality)}
                    </Box>

                    <Box sx={{ width: 100, height: 100 }}>
                      <CourtScatterSinglePoint
                        p={videoPoint}
                        teamColor={teamColor}
                      />
                    </Box>
                  </Box>
                ) : null
              }
              showAi={true}
              aiDrawerMode="split"
              focusedFilter={null}
              packageId={packageId}
              controlsPlacement="left"
              row={videoRow}
              rows={rows}
              activeShotNumber={3}
              beginningScore={videoRow.scoreBefore}
              endingScore={videoRow.scoreAfter}
              showCondensedTimeline={true}
              showPopoutButton={true}
              showScoreEtc={false}
              showTitle={false}
              fontColor={"black"}
              firefightsClips={firefightsClips}
              firefightRallySet={firefightRallySet}
            />
          )}
        </FloatingPanel>
      )}
    </>
  );
}

/* ----------------------------- Recharts court scatter ----------------------------- */

// type PlotPoint = ThirdShotPoint & {
//   plotU: number;
//   plotV: number;
//   usesCourt: boolean;
// };

function CourtScatterRecharts(props: {
  points: PlotPoint[];
  hoverTarget: ThirdShotHoverTarget | null;
  teamColor: (team: 0 | 1, alpha?: number) => string;
  onPickPoint: (
    p: PlotPoint,
    e: React.MouseEvent<SVGGElement, MouseEvent>,
  ) => void;
  // NEW:
  onHoverPoint: (
    phase: "enter" | "move" | "leave",
    p: PlotPoint,
    e: React.MouseEvent<SVGGElement, MouseEvent>,
  ) => void;
  // ✅ NEW
  onLeaveChart: () => void;
  onMoveChart?: (clientX: number, clientY: number) => void;
  shotCategoryFilter: ShotCategoryFilter;
}) {
  const X_PAD = 3;
  const X_MIN = -X_PAD;
  const X_MAX = COURT_WIDTH + X_PAD;

  const Y_PAD = 6;
  const Y_MIN = -Y_PAD;
  const Y_MAX = COURT_LENGTH + Y_PAD;

  const DOMAIN_W = X_MAX - X_MIN; // 26
  const DOMAIN_H = Y_MAX - Y_MIN; // 44

  const showMovement = true;

  const {
    points,
    teamColor,
    onPickPoint,
    onHoverPoint,
    onLeaveChart,
    onMoveChart,
    shotCategoryFilter,
    hoverTarget,
  } = props;

  const hasHoverTarget = hoverTarget != null;

  const dataT0 = React.useMemo(
    () => points.filter((p) => p.hitterTeam === 0),
    [points],
  );
  const dataT1 = React.useMemo(
    () => points.filter((p) => p.hitterTeam === 1),
    [points],
  );

  // const W = 330;
  // const H = 460;
  // const M = 14;

  const courtStroke = "rgba(0,0,0,0.18)";
  const courtStrokeW = 2;

  return (
    <Box
      sx={{
        outline: "none",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 1.5,
        overflow: "hidden",
        width: "100%",
        height: "100%",
        bgcolor: "rgba(255,255,255,0.03)",
        position: "relative",
        // ✅ kill Chrome focus ring on the internal SVG / wrappers
        "& .recharts-wrapper:focus, & .recharts-wrapper:focus-visible": {
          outline: "none !important",
        },
        "& .recharts-surface:focus, & .recharts-surface:focus-visible": {
          outline: "none !important",
        },
        "& svg:focus, & svg:focus-visible, & svg g:focus": {
          outline: "none !important",
        },
      }}
      onMouseLeave={() => {
        props.onLeaveChart();
      }}
      onMouseMove={(e: any) => {
        props.onMoveChart?.(e.clientX, e.clientY);
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, zIndex: 2, outline: "none" }}>
        {/* Center an aspect-locked chart that grows as large as possible */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            height: "100%",
            display: "grid",
            placeItems: "center",
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              // take the limiting dimension while preserving aspect
              width: "auto",
              height: "100%",
              maxWidth: "100%",
              //maxHeight: "100%",
              aspectRatio: `${DOMAIN_W}/${DOMAIN_H}`, // 20/44
              // IMPORTANT: ResponsiveContainer needs a real size
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                // margin={{ top: M, right: M, bottom: M, left: M }}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                overflow="visible"
              >
                <XAxis
                  type="number"
                  dataKey="plotX"
                  //domain={[-COURT_WIDTH * PAD, COURT_WIDTH * (1 + PAD)]}
                  domain={[X_MIN, X_MAX]}
                  hide
                  allowDataOverflow
                />

                <YAxis
                  type="number"
                  dataKey="plotY"
                  domain={[Y_MIN, Y_MAX]} //0, COURT_LENGTH]}
                  reversed
                  allowDataOverflow
                  hide={true}
                  axisLine={false}
                  tickLine={false}
                  // width={46}
                  ticks={null}
                  //   ticks={[0, 15, 22, 29, 44]}
                  //   tickFormatter={(y: number) => `${Math.round(y)}`}
                  tick={{ fontSize: 11, fill: "rgba(0,0,0,1)" }}
                />

                {/* Court border (4 segments) */}
                <ReferenceLine
                  ifOverflow="extendDomain"
                  stroke={courtStroke}
                  strokeWidth={courtStrokeW}
                  segment={[
                    { x: 0, y: 0 },
                    { x: COURT_WIDTH, y: 0 },
                  ]}
                />
                <ReferenceLine
                  ifOverflow="extendDomain"
                  stroke={courtStroke}
                  strokeWidth={courtStrokeW}
                  segment={[
                    { x: 0, y: COURT_LENGTH },
                    { x: COURT_WIDTH, y: COURT_LENGTH },
                  ]}
                />
                <ReferenceLine
                  ifOverflow="extendDomain"
                  stroke={courtStroke}
                  strokeWidth={courtStrokeW}
                  segment={[
                    { x: 0, y: 0 },
                    { x: 0, y: COURT_LENGTH },
                  ]}
                />
                <ReferenceLine
                  ifOverflow="extendDomain"
                  stroke={courtStroke}
                  strokeWidth={courtStrokeW}
                  segment={[
                    { x: COURT_WIDTH, y: 0 },
                    { x: COURT_WIDTH, y: COURT_LENGTH },
                  ]}
                />

                {/* Net + kitchen */}
                <ReferenceLine
                  //   y={COURT_LENGTH / 2}
                  //   x={0}
                  ifOverflow="extendDomain"
                  stroke="rgba(0,0,0,0.22)"
                  strokeWidth={2}
                  segment={[
                    { x: 0, y: COURT_LENGTH / 2 },
                    { x: COURT_WIDTH, y: COURT_LENGTH / 2 },
                  ]}
                />
                <ReferenceLine
                  //y={COURT_LENGTH / 2 - 7}
                  segment={[
                    { x: 0, y: COURT_LENGTH / 2 - 7 },
                    { x: COURT_WIDTH, y: COURT_LENGTH / 2 - 7 },
                  ]}
                  ifOverflow="extendDomain"
                  stroke="rgba(0,0,0,0.12)"
                  strokeWidth={1.5}
                />
                <ReferenceLine
                  //   y={COURT_LENGTH / 2 + 7}
                  segment={[
                    { x: 0, y: COURT_LENGTH / 2 + 7 },
                    { x: COURT_WIDTH, y: COURT_LENGTH / 2 + 7 },
                  ]}
                  ifOverflow="extendDomain"
                  stroke="rgba(0,0,0,0.12)"
                  strokeWidth={1.5}
                />

                {/* Service centerline (two halves) */}
                <ReferenceLine
                  ifOverflow="extendDomain"
                  stroke="rgba(0,0,0,0.12)"
                  strokeWidth={1.5}
                  segment={[
                    { x: COURT_WIDTH / 2, y: 0 },
                    { x: COURT_WIDTH / 2, y: COURT_LENGTH / 2 - 7 },
                  ]}
                />
                <ReferenceLine
                  ifOverflow="extendDomain"
                  stroke="rgba(0,0,0,0.12)"
                  strokeWidth={1.5}
                  segment={[
                    { x: COURT_WIDTH / 2, y: COURT_LENGTH / 2 + 7 },
                    { x: COURT_WIDTH / 2, y: COURT_LENGTH },
                  ]}
                />

                {/* <RechartsTooltip cursor={false} content={<ThirdShotTooltip />} /> */}

                {/* next-shot preview lines */}
                {points.map((p) => {
                  if (
                    !Number.isFinite(p.nextCourtX) ||
                    !Number.isFinite(p.nextCourtY)
                  ) {
                    return null;
                  }

                  const isHighlighted = pointMatchesHoverTarget(
                    p,
                    props.hoverTarget,
                  );
                  const dimmed = hasHoverTarget && !isHighlighted;

                  return (
                    <ReferenceLine
                      key={`next-${p.rallyIndex}-${p.pointKey ?? ""}`}
                      ifOverflow="extendDomain"
                      segment={[
                        { x: p.plotX, y: p.plotY },
                        {
                          x: p.nextCourtX as number,
                          y: p.nextCourtY as number,
                        },
                      ]}
                      // stroke={teamColor(
                      //   p.hitterTeam,
                      //   shotCategoryFilter === "all"
                      //     ? p.shotCategory === "drive"
                      //       ? 0.24
                      //       : 0.32
                      //     : 0.24,
                      // )}
                      // strokeWidth={
                      //   shotCategoryFilter === "all"
                      //     ? p.shotCategory === "drive"
                      //       ? p.shotWeight * 1.6
                      //       : p.shotWeight * 2.05
                      //     : p.shotCategory === "drive"
                      //       ? p.shotWeight * 1.9
                      //       : p.shotWeight * 2.15
                      // }

                      stroke={teamColor(
                        p.hitterTeam,
                        dimmed
                          ? 0.05
                          : p.shotCategory === "drive"
                            ? 0.34
                            : 0.42,
                      )}
                      strokeWidth={
                        dimmed
                          ? 1
                          : shotCategoryFilter === "all"
                            ? p.shotCategory === "drive"
                              ? p.shotWeight * 1.6
                              : p.shotWeight * 2.05
                            : p.shotCategory === "drive"
                              ? p.shotWeight * 1.9
                              : p.shotWeight * 2.15
                      }
                      strokeDasharray={
                        p.shotCategory === "drop" ? "4 5" : undefined
                      }
                    />
                  );
                })}

                <Scatter
                  name="Team 0"
                  data={dataT0}
                  shape={(p: any) => (
                    <DotShape
                      {...p}
                      teamColor={teamColor}
                      hoverTarget={props.hoverTarget}
                      onPickPoint={onPickPoint}
                      onHoverPoint={props.onHoverPoint}
                    />
                  )}
                  isAnimationActive={false}
                />
                <Scatter
                  name="Team 1"
                  data={dataT1}
                  shape={(p: any) => (
                    <DotShape
                      {...p}
                      teamColor={teamColor}
                      onPickPoint={onPickPoint}
                      onHoverPoint={props.onHoverPoint}
                      hoverTarget={props.hoverTarget}
                    />
                  )}
                  isAnimationActive={false}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
/**
 * Draws court lines using recharts axis scales.
 * Recharts passes in axis maps with scale functions.
 */

function CourtLines(props: any) {
  const { xAxisMap, yAxisMap } = props;

  const xAxis: any = xAxisMap && Object.values(xAxisMap)[0];
  const yAxis: any = yAxisMap && Object.values(yAxisMap)[0];
  const sx = xAxis?.scale;
  const sy = yAxis?.scale;
  if (typeof sx !== "function" || typeof sy !== "function") return null;

  const X = (x: number) => sx(x);
  const Y = (y: number) => sy(y);

  const xL = X(0);
  const xR = X(COURT_WIDTH);
  const yT = Y(0);
  const yB = Y(COURT_LENGTH);

  const netY = COURT_LENGTH / 2; // 22
  const kTop = netY - 7; // 15
  const kBot = netY + 7; // 29
  const midX = COURT_WIDTH / 2; // 10

  return (
    <g style={{ pointerEvents: "none" }}>
      {/* subtle plot bg */}
      <rect
        x={Math.min(xL, xR)}
        y={Math.min(yT, yB)}
        width={Math.abs(xR - xL)}
        height={Math.abs(yB - yT)}
        fill="rgba(0,0,0,0.01)"
      />

      {/* outer border */}
      <rect
        x={Math.min(xL, xR)}
        y={Math.min(yT, yB)}
        width={Math.abs(xR - xL)}
        height={Math.abs(yB - yT)}
        fill="none"
        stroke="rgba(0,0,0,0.18)"
        strokeWidth={2}
      />

      {/* net */}
      <line
        x1={X(0)}
        y1={Y(netY)}
        x2={X(COURT_WIDTH)}
        y2={Y(netY)}
        stroke="rgba(0,0,0,0.22)"
        strokeWidth={2}
      />

      {/* kitchen lines */}
      <line
        x1={X(0)}
        y1={Y(kTop)}
        x2={X(COURT_WIDTH)}
        y2={Y(kTop)}
        stroke="rgba(0,0,0,1)"
        strokeWidth={1.5}
      />
      <line
        x1={X(0)}
        y1={Y(kBot)}
        x2={X(COURT_WIDTH)}
        y2={Y(kBot)}
        stroke="rgba(0,0,0,1)"
        strokeWidth={1.5}
      />

      {/* service centerline segments */}
      <line
        x1={X(midX)}
        y1={Y(0)}
        x2={X(midX)}
        y2={Y(kTop)}
        stroke="rgba(0,0,0,1)"
        strokeWidth={1.5}
      />
      <line
        x1={X(midX)}
        y1={Y(kBot)}
        x2={X(midX)}
        y2={Y(COURT_LENGTH)}
        stroke="rgba(0,0,0,1)"
        strokeWidth={1.5}
      />
    </g>
  );
}
function DotShape(
  props: any & {
    teamColor: (team: 0 | 1, alpha?: number) => string;
    hoverTarget?: ThirdShotHoverTarget | null;
    onPickPoint?: (
      p: PlotPoint,
      e: React.MouseEvent<SVGGElement, MouseEvent>,
    ) => void;
    onHoverPoint?: (
      phase: "enter" | "move" | "leave",
      p: PlotPoint,
      e: React.MouseEvent<SVGGElement, MouseEvent>,
    ) => void;
  },
) {
  const { cx, cy, payload, teamColor, onPickPoint, onHoverPoint, hoverTarget } =
    props;
  if (!payload || !Number.isFinite(cx) || !Number.isFinite(cy)) return null;

  const p: PlotPoint = payload;

  const highlighted = pointMatchesHoverTarget(p, props.hoverTarget ?? null);
  const dimmed = props.hoverTarget != null && !highlighted;
  const pointHovered =
    props.hoverTarget?.kind === "point" &&
    props.hoverTarget.rallyIndex === p.rallyIndex;

  const stroke = teamColor(p.hitterTeam, 0.95);
  const fill =
    p.wonRally == null
      ? "rgba(255,255,255,0.10)"
      : p.wonRally
        ? teamColor(p.hitterTeam, 0.55)
        : "rgba(255,255,255,0.08)";

  // Recharts "dashed circle" is easiest via strokeDasharray.
  const dash = p.wonRally == null ? "4 3" : undefined;

  const showX = Boolean((p as any).lostOnThirdShot);

  return (
    <g
      data-demo="shot-location-point"
      opacity={dimmed ? 0.18 : 1}
      style={{ cursor: onPickPoint ? "pointer" : "default" }}
      pointerEvents={dimmed ? "none" : "auto"}
      onClick={(e) => {
        e.stopPropagation();
        if (dimmed) {
          return;
        }
        onPickPoint?.(p, e);
      }}
      onMouseEnter={(e) => {
        if (dimmed) {
          return;
        }
        props.onHoverPoint?.("enter", p, e);
      }}
      onMouseMove={(e) => {
        if (dimmed) {
          return;
        }
        props.onHoverPoint?.("move", p, e);
      }}
      onMouseLeave={(e) => {
        if (dimmed) {
          return;
        }
        props.onHoverPoint?.("leave", p, e);
      }}
    >
      <circle
        cx={cx}
        cy={cy}
        // r={6.5}
        stroke={stroke}
        fill={fill}
        // strokeWidth={2}
        r={pointHovered ? 8 : 6}
        strokeWidth={pointHovered ? 3 : 2}
        strokeDasharray={dash}
      />
      {showX && (
        <g>
          <line
            x1={cx - 4.2}
            y1={cy - 4.2}
            x2={cx + 4.2}
            y2={cy + 4.2}
            stroke="rgba(0,0,0,0.85)"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
          <line
            x1={cx - 4.2}
            y1={cy + 4.2}
            x2={cx + 4.2}
            y2={cy - 4.2}
            stroke="rgba(0,0,0,0.85)"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </g>
      )}
    </g>
  );
}

function CourtScatterSinglePoint(props: {
  p: PlotPoint;
  teamColor: (team: 0 | 1, alpha?: number) => string;
}) {
  const { p, teamColor } = props;

  const stroke = "rgba(0,0,0,0.35)";
  const strokeW = 1.2;

  const X_PAD = 3;
  const X_MIN = -X_PAD;
  const X_MAX = COURT_WIDTH + X_PAD;

  const Y_PAD = 6;
  const Y_MIN = -Y_PAD;
  const Y_MAX = COURT_LENGTH + Y_PAD;

  function insetTrajectoryStyle(p: PlotPoint) {
    const isDrive = p.shotCategory === "drive";
    const baseWeight = p.shotWeight ?? 1;

    return {
      stroke: teamColor(p.hitterTeam, isDrive ? 0.26 : 0.34),
      strokeWidth: isDrive ? baseWeight * 1.9 : baseWeight * 2.2,
      strokeDasharray: p.shotCategory === "drop" ? "4 5" : undefined,
      endFill: teamColor(p.hitterTeam, isDrive ? 0.28 : 0.18),
      endStroke: teamColor(p.hitterTeam, isDrive ? 0.9 : 0.6),
      endR: isDrive ? 3.8 : 3.2,
    };
  }

  const hasNext =
    Number.isFinite(p.nextCourtX) && Number.isFinite(p.nextCourtY);

  const traj = insetTrajectoryStyle(p);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <XAxis type="number" dataKey="plotX" domain={[X_MIN, X_MAX]} hide />
        <YAxis
          type="number"
          dataKey="plotY"
          domain={[Y_MIN, Y_MAX]}
          reversed
          hide
        />

        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: 0 },
            { x: COURT_WIDTH, y: 0 },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: COURT_LENGTH },
            { x: COURT_WIDTH, y: COURT_LENGTH },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: 0 },
            { x: 0, y: COURT_LENGTH },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: COURT_WIDTH, y: 0 },
            { x: COURT_WIDTH, y: COURT_LENGTH },
          ]}
        />

        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: COURT_LENGTH / 2 },
            { x: COURT_WIDTH, y: COURT_LENGTH / 2 },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: 15 },
            { x: COURT_WIDTH, y: 15 },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: 29 },
            { x: COURT_WIDTH, y: 29 },
          ]}
        />

        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: COURT_WIDTH / 2, y: 0 },
            { x: COURT_WIDTH / 2, y: 15 },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: COURT_WIDTH / 2, y: 29 },
            { x: COURT_WIDTH / 2, y: COURT_LENGTH },
          ]}
        />

        {hasNext && (
          <>
            <ReferenceLine
              ifOverflow="extendDomain"
              segment={[
                { x: p.plotX, y: p.plotY },
                { x: p.nextCourtX as number, y: p.nextCourtY as number },
              ]}
              stroke={traj.stroke}
              strokeWidth={traj.strokeWidth}
              strokeDasharray={traj.strokeDasharray}
            />

            <Scatter
              data={[
                {
                  plotX: p.nextCourtX as number,
                  plotY: p.nextCourtY as number,
                },
              ]}
              shape={(sp: any) => {
                const { cx, cy } = sp;
                if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={traj.endR}
                    fill={traj.endFill}
                    stroke={traj.endStroke}
                    strokeWidth={1.2}
                  />
                );
              }}
            />
          </>
        )}

        <Scatter
          data={[p]}
          shape={(sp: any) => <DotShape {...sp} teamColor={teamColor} />}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function CourtScatterSinglePoint_old(props: {
  p: PlotPoint;
  teamColor: (team: 0 | 1, alpha?: number) => string;
}) {
  const { p, teamColor } = props;

  const stroke = "rgba(0,0,0,0.35)";
  const strokeW = 1.2;

  const X_PAD = 3;
  const X_MIN = -X_PAD;
  const X_MAX = COURT_WIDTH + X_PAD;

  const Y_PAD = 6;
  const Y_MIN = -Y_PAD;
  const Y_MAX = COURT_LENGTH + Y_PAD;

  const DOMAIN_W = X_MAX - X_MIN; // 26
  const DOMAIN_H = Y_MAX - Y_MIN; // 44

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <XAxis type="number" dataKey="plotX" domain={[X_MIN, X_MAX]} hide />
        <YAxis
          type="number"
          dataKey="plotY"
          domain={[Y_MIN, Y_MAX]}
          reversed
          hide
        />

        {/* border */}
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: 0 },
            { x: COURT_WIDTH, y: 0 },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: COURT_LENGTH },
            { x: COURT_WIDTH, y: COURT_LENGTH },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: 0 },
            { x: 0, y: COURT_LENGTH },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: COURT_WIDTH, y: 0 },
            { x: COURT_WIDTH, y: COURT_LENGTH },
          ]}
        />

        {/* net + kitchen */}
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: COURT_LENGTH / 2 },
            { x: COURT_WIDTH, y: COURT_LENGTH / 2 },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: 15 },
            { x: COURT_WIDTH, y: 15 },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: 0, y: 29 },
            { x: COURT_WIDTH, y: 29 },
          ]}
        />

        {/* center line (service boxes) */}
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: COURT_WIDTH / 2, y: 0 },
            { x: COURT_WIDTH / 2, y: 15 },
          ]}
        />
        <ReferenceLine
          ifOverflow="extendDomain"
          stroke={stroke}
          strokeWidth={strokeW}
          segment={[
            { x: COURT_WIDTH / 2, y: 29 },
            { x: COURT_WIDTH / 2, y: COURT_LENGTH },
          ]}
        />

        <Scatter
          data={[p]}
          shape={(sp: any) => <DotShape {...sp} teamColor={teamColor} />}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function ThirdShotTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const p: any = payload?.[0]?.payload;
  if (!p) return null;

  const wl = p.wonRally === true ? "WIN" : p.wonRally === false ? "LOSS" : "?";

  return (
    <Box
      sx={{
        bgcolor: "rgba(20,20,20,0.92)",
        color: "white", //"rgba(255,255,255,0.92)",
        px: 1,
        py: 0.75,
        borderRadius: 1,
        border: "1px solid rgba(255,255,255,0.14)",
        fontSize: 12,
        lineHeight: 1.25,
        maxWidth: 240,
      }}
    >
      <Box sx={{ fontWeight: 800 }}>
        R{p.rallyIndex + 1} · T{p.hitterTeam} · {wl}
      </Box>

      <Box sx={{ opacity: 0.85 }}>
        x={Number(p.plotX).toFixed(2)} · y={Number(p.plotY).toFixed(2)}
      </Box>
    </Box>
  );
}

/* ----------------------------- Sidebar bits ----------------------------- */

function Legend(props: { teamColor: (team: 0 | 1, alpha?: number) => string }) {
  const { teamColor } = props;
  return (
    <Box sx={{ fontSize: 12, lineHeight: 1.3, opacity: 0.9 }}>
      <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>
        Legend
      </Typography>

      <LegendRow
        label="Win (fill)"
        swatchSx={{
          border: `2px solid ${teamColor(0, 0.9)}`,
          bgcolor: teamColor(0, 0.55),
        }}
      />
      <LegendRow
        label="Loss (hollow-ish)"
        swatchSx={{
          border: `2px solid ${teamColor(0, 0.9)}`,
          bgcolor: "rgba(255,255,255,0.08)",
        }}
      />
      {/* <LegendRow
        label="Unknown (dashed)"
        swatchSx={{
          border: "2px dashed rgba(255,255,255,0.35)",
          bgcolor: "rgba(255,255,255,0.06)",
        }}
      /> */}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.6 }}>
        <Box sx={{ width: 14, height: 14, position: "relative" }}>
          <svg
            //component="svg"
            viewBox="0 0 14 14"
            style={{ width: 14, height: 14, display: "block" }}
          >
            <line
              x1="3"
              y1="3"
              x2="11"
              y2="11"
              stroke="rgba(0,0,0,0.85)"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="3"
              y1="11"
              x2="11"
              y2="3"
              stroke="rgba(0,0,0,0.85)"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </Box>
        <Typography variant="body2">Black X = lost on 3rd</Typography>
      </Box>

      {/* <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.75 }}>
        Stroke = hitter team
      </Typography> */}
    </Box>
  );
}

function LegendRow(props: { label: string; swatchSx: any }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
      <Box
        sx={{ width: 14, height: 14, borderRadius: "50%", ...props.swatchSx }}
      />
      <Typography variant="body2">{props.label}</Typography>
    </Box>
  );
}

function OutcomeCells(props: {
  stats: ShotOutcomeStats;
  team: Team01;
  teamColor: (team: 0 | 1, alpha?: number) => string;
  playerId: number;
  rowPoints: PlotPoint[];
  shotCategory: ShotCategoryFilter;
  hoverTarget: ThirdShotHoverTarget | null;
  onHoverTarget: (target: ThirdShotHoverTarget | null) => void;
  pinnedTarget: ThirdShotHoverTarget | null;
  onPinnedTarget: (target: ThirdShotHoverTarget | null) => void;
  onActivateTarget?: (target: ThirdShotHoverTarget) => void;
}) {
  const {
    stats,
    team,
    teamColor,
    playerId,
    shotCategory,
    hoverTarget,
    onHoverTarget,
    rowPoints,
    pinnedTarget,
    onPinnedTarget,
    onActivateTarget,
  } = props;

  const targetBase = {
    kind: "stat" as const,
    team,
    playerId,
    shotCategory,
  };

  const pointForThisRow =
    hoverTarget?.kind === "point"
      ? (rowPoints.find((p) => p.rallyIndex === hoverTarget.rallyIndex) ?? null)
      : null;

  const togglePinnedCell = (
    metric:
      | "attempts"
      | "successes"
      | "rallyWins"
      | "rallyLosses"
      | "errors"
      | "quality",
  ) => ({
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();

      const next: ThirdShotHoverTarget = {
        ...targetBase,
        metric,
      };

      const same =
        pinnedTarget?.kind === "stat" &&
        pinnedTarget.team === next.team &&
        pinnedTarget.playerId === next.playerId &&
        pinnedTarget.shotCategory === next.shotCategory &&
        pinnedTarget.metric === next.metric;

      // onPinnedTarget(same ? null : next);

      if (same) {
        onPinnedTarget(null);
        return;
      }

      onActivateTarget?.(next);
      onPinnedTarget(next);
    },
  });

  const cellMatchesPointHover = (
    metric:
      | "attempts"
      | "successes"
      | "rallyWins"
      | "rallyLosses"
      | "errors"
      | "quality",
  ) => {
    if (!pointForThisRow) return false;
    if (metric === "attempts") return true;
    if (metric === "rallyWins") return pointForThisRow.wonRally === true;
    if (metric === "rallyLosses") return pointForThisRow.wonRally === false;
    if (metric === "successes") return !pointForThisRow.lostOnThirdShot;
    if (metric === "errors") return pointForThisRow.lostOnThirdShot;
    if (metric === "quality") return true;
    return false;
  };

  const cellSx = (
    metric:
      | "attempts"
      | "successes"
      | "rallyWins"
      | "rallyLosses"
      | "errors"
      | "quality",
  ) => {
    const statHover =
      hoverTarget?.kind === "stat" &&
      hoverTarget.team === team &&
      hoverTarget.playerId === playerId &&
      hoverTarget.shotCategory === shotCategory &&
      hoverTarget.metric === metric;

    const pointHover = cellMatchesPointHover(metric);

    return {
      cursor: "default",
      bgcolor: statHover || pointHover ? teamColor(team, 0.16) : "transparent",
      fontWeight: statHover || pointHover ? 900 : 400,
    };
  };

  // const clickCell = (
  //   metric: "attempts" | "rallyWins" | "rallyLosses" | "errors" | "quality",
  // ) => ({
  //   onClick: (e: React.MouseEvent) => {
  //     e.stopPropagation();
  //     onPinnedTarget((prev) => {
  //       const next = { ...targetBase, metric };
  //       return JSON.stringify(prev) === JSON.stringify(next) ? null : next;
  //     });
  //   },
  // });

  const hoverCell = (
    metric:
      | "attempts"
      | "successes"
      | "rallyWins"
      | "rallyLosses"
      | "errors"
      | "quality",
  ) => ({
    onMouseEnter: () => onHoverTarget({ ...targetBase, metric }),
    onMouseLeave: () => onHoverTarget(null),
  });

  return (
    <>
      <TableCell
        align="right"
        {...hoverCell("attempts")}
        {...togglePinnedCell("attempts")}
        sx={cellSx("attempts")}
      >
        {stats.attempts}
      </TableCell>

      <TableCell
        align="right"
        {...hoverCell("successes")}
        {...togglePinnedCell("successes")}
        sx={cellSx("successes")}
      >
        {countPct(stats.attempts - stats.errors, stats.attempts)}
      </TableCell>

      <TableCell
        align="right"
        {...hoverCell("errors")}
        {...togglePinnedCell("errors")}
        sx={cellSx("errors")}
      >
        {countPct(stats.errors, stats.attempts)}
      </TableCell>

      <TableCell
        align="right"
        {...hoverCell("rallyWins")}
        {...togglePinnedCell("rallyWins")}
        sx={cellSx("rallyWins")}
      >
        {countPct(stats.rallyWins, stats.attempts)}
      </TableCell>

      <TableCell
        align="right"
        {...hoverCell("rallyLosses")}
        {...togglePinnedCell("rallyLosses")}
        sx={cellSx("rallyLosses")}
      >
        {countPct(stats.rallyLosses, stats.attempts)}
      </TableCell>

      {/* <TableCell align="right" {...hoverCell("quality")} sx={cellSx("quality")}>
        {avgQuality(stats)}
      </TableCell> */}
    </>
  );
}

function TeamSummary(props: {
  title: string;
  points: PlotPoint[]; // filtered points for headline totals
  team: 0 | 1;
  teamColor: (team: 0 | 1, alpha?: number) => string;

  playerStats: PlayerStatRow[]; // unfiltered per-player breakdown
  selectedPlayerId: number | null; // highlight this player row
  renderPlayerIdentity: RenderPlayerIdentity;
  hoverTarget: ThirdShotHoverTarget | null;
  onSelectPlayer: (pid: number | null) => void;
  onHoverTarget: (target: ThirdShotHoverTarget | null) => void;
  pinnedTarget: ThirdShotHoverTarget | null;
  onPinnedTarget: (target: ThirdShotHoverTarget | null) => void;
  onActivateTarget: (target: ThirdShotHoverTarget) => void;
}) {
  const {
    title,
    points,
    team,
    teamColor,
    playerStats,
    selectedPlayerId,
    renderPlayerIdentity,
    hoverTarget,
    onHoverTarget,
    onSelectPlayer,
    pinnedTarget,
    onPinnedTarget,
    onActivateTarget,
  } = props;
  const win = points.filter((p) => p.wonRally === true).length;
  const loss = points.filter((p) => p.wonRally === false).length;
  const unk = points.filter((p) => p.wonRally == null).length;

  const drives = points.filter((p) => p.shotCategory === "drive").length;
  const drops = points.filter((p) => p.shotCategory === "drop").length;
  const otherShots = points.filter((p) => p.shotCategory == null).length;

  const getRowPoints = (row: PlayerStatRow, shotCategory: ShotCategoryFilter) =>
    points.filter(
      (p) =>
        p.hitterTeam === team &&
        p.hitterPlayerId === row.playerId &&
        (shotCategory === "all" || p.shotCategory === shotCategory),
    );

  return (
    <Box
      sx={{
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: "2px",
        paddingLeft: "2px",
      }}
    >
      <Typography sx={{ fontWeight: 900, color: teamColor(team, 0.95) }}>
        {title}
      </Typography>
      {/* <Typography sx={{ fontSize: 11, opacity: 0.68, mb: 0.5 }}>
        Percentages use attempts in that row as the denominator. Quality is
        average shot-quality score.
      </Typography> */}

      <TableContainer sx={{ mt: 0.75 }}>
        <Table
          size="small"
          sx={{
            "& th": {
              fontSize: 11,
              fontWeight: 900,
              whiteSpace: "nowrap",
              px: 0.75,
              py: 0.5,
              borderBottom: "1px solid rgba(0,0,0,0.14)",
            },
            "& td": {
              fontSize: 11,
              px: 0.75,
              py: 0.45,
              borderBottom: "1px solid rgba(0,0,0,0.07)",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Player</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Attempts</TableCell>
              <TableCell align="right">Successes</TableCell>
              <TableCell align="right">3rd Errors</TableCell>
              <TableCell align="right">Rally Wins</TableCell>
              <TableCell align="right">Rally Losses</TableCell>
              {/* <TableCell align="right">Quality</TableCell> */}
            </TableRow>
          </TableHead>

          <TableBody>
            {playerStats.map((row) => {
              const selected = selectedPlayerId === row.playerId;

              const rowMatchesPointHover = (
                shotCategory: ShotCategoryFilter,
              ) => {
                if (hoverTarget?.kind !== "point") return false;

                return points.some(
                  (p) =>
                    p.rallyIndex === hoverTarget.rallyIndex &&
                    p.hitterTeam === team &&
                    p.hitterPlayerId === row.playerId &&
                    (shotCategory === "all" || p.shotCategory === shotCategory),
                );
              };

              const rowBg = (shotCategory: ShotCategoryFilter) =>
                rowMatchesPointHover(shotCategory)
                  ? teamColor(team, 0.14)
                  : "transparent";

              const playerCell = (
                <TableCell
                  data-happy-path="shot-location-player-filter"
                  data-shot-location-team={team}
                  data-shot-location-player-id={row.playerId}
                  data-selected={selected ? "true" : "false"}
                  onClick={() =>
                    onSelectPlayer(
                      selectedPlayerId === row.playerId ? null : row.playerId,
                    )
                  }
                  //onSelectPlayer(row.playerId)}
                  rowSpan={3}
                  // sx={{
                  //   verticalAlign: "top",
                  //   bgcolor: selected ? teamColor(team, 0.12) : "transparent",
                  // }}
                  sx={{
                    cursor: "pointer",
                    bgcolor:
                      selectedPlayerId === row.playerId
                        ? teamColor(team, 0.14)
                        : "transparent",
                    "&:hover": {
                      bgcolor: teamColor(team, 0.1),
                    },
                  }}
                >
                  {renderPlayerIdentity
                    ? renderPlayerIdentity({
                        playerId: row.playerId,
                        labelOnly: false,
                        size: 28,
                        borderRadius: 5,
                        fontSize: 11,
                      })
                    : `p${row.playerId}`}
                </TableCell>
              );

              return (
                <React.Fragment key={row.playerId}>
                  <TableRow
                    sx={{
                      bgcolor: rowBg("all"),
                      //  pointHoverForThisPlayer
                      //   ? teamColor(team, 0.12)
                      //   : "transparent",
                    }}
                  >
                    {playerCell}
                    <TableCell sx={{ fontWeight: 900 }}>All</TableCell>
                    <OutcomeCells
                      rowPoints={getRowPoints(row, "all")}
                      stats={row.total}
                      team={team}
                      teamColor={teamColor}
                      playerId={row.playerId}
                      shotCategory="all"
                      hoverTarget={hoverTarget}
                      onHoverTarget={onHoverTarget}
                      pinnedTarget={pinnedTarget}
                      onPinnedTarget={onPinnedTarget}
                      onActivateTarget={onActivateTarget}
                    />
                  </TableRow>

                  <TableRow
                    sx={{
                      bgcolor: rowBg("drop"),
                    }}
                  >
                    <TableCell>Drop</TableCell>
                    <OutcomeCells
                      stats={row.drop}
                      rowPoints={getRowPoints(row, "drop")}
                      team={team}
                      teamColor={teamColor}
                      playerId={row.playerId}
                      shotCategory="drop"
                      hoverTarget={hoverTarget}
                      onHoverTarget={onHoverTarget}
                      pinnedTarget={pinnedTarget}
                      onPinnedTarget={onPinnedTarget}
                      onActivateTarget={onActivateTarget}
                    />
                  </TableRow>

                  <TableRow
                    sx={{
                      bgcolor: rowBg("drive"),
                    }}
                  >
                    <TableCell>Drive</TableCell>
                    <OutcomeCells
                      stats={row.drive}
                      teamColor={teamColor}
                      rowPoints={getRowPoints(row, "drive")}
                      team={team}
                      playerId={row.playerId}
                      shotCategory="drive"
                      hoverTarget={hoverTarget}
                      onHoverTarget={onHoverTarget}
                      pinnedTarget={pinnedTarget}
                      onPinnedTarget={onPinnedTarget}
                      onActivateTarget={onActivateTarget}
                    />
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
